import type { VerifyInstallationResult } from 'services/installationApi'

/**
 * Turn a failed verification into the alert's severity, heading and body.
 *
 * The page used to render ONE message for every failure: the server's sentence
 * plus a hardcoded "Confirm the snippet is in your site's <head> and the page is
 * published, then re-verify." That remedy is only correct for `snippet_missing`.
 * For `timeout` or `unreachable` the page was never fetched at all, so telling
 * someone to go re-check their markup sends them to inspect a file that isn't the
 * problem — the domain didn't resolve. For `http_403` the fix is usually a WAF or
 * bot rule, not the snippet. The server already knew which case it was
 * (installation-verification.service.ts) and the code now reaches the client, so
 * the guidance can match.
 *
 * EVERY failure is an ERROR, including `snippet_missing`.
 *
 * That one used to be a WARNING, on the reasoning that "not pasted yet" is the
 * expected state right after saving a URL, so red would cry wolf during setup.
 * That was sound only while the page auto-verified on mount — the check ran
 * whether or not you had done anything, so a failure genuinely might mean "not
 * finished yet". Verification is now only ever triggered by clicking Verify, and
 * clicking it is a claim that the code IS installed. A negative answer to that
 * claim is a failure, not a progress report. Amber understated it.
 *
 * The server's own sentence is always kept as the first line (it carries the
 * specifics, e.g. the exact status code); the remedy is appended only where one
 * applies.
 */
export type VerifyFeedback = {
  severity: 'warning' | 'error'
  title: string
  description: string
}

const SNIPPET_REMEDY =
  "Confirm the snippet is in your site's <head> and the page is published, then re-verify."

const UNREACHABLE_REMEDY =
  'Check the URL is correct, the domain resolves publicly, and the page is reachable without a login.'

const BLOCKED_REMEDY =
  'A firewall or bot-protection rule may be blocking our check. Allow requests from Vera-Installation-Verifier, then re-verify.'

export const getVerifyFeedback = (result: VerifyInstallationResult): VerifyFeedback => {
  const serverMessage = result.message?.trim() || 'We could not verify the installation.'
  const code = result.error_code ?? ''

  const withRemedy = (remedy: string) => `${serverMessage} ${remedy}`

  // A store-wide password gate reads as a missing snippet from the probe's side —
  // we follow the redirect, land on a login page, and find nothing. Saying "recheck
  // step 2" there sends someone to re-paste code that was never the problem.
  if (code === 'password_protected') {
    return {
      severity: 'error',
      title: 'Your store is password-protected',
      description: withRemedy(
        'Vera checks the page the way a customer would, from outside your network, so it only sees the password screen. Remove the store password (or publish the store) and verify again. Your snippet may already be correct.',
      ),
    }
  }

  if (code === 'snippet_missing') {
    return {
      severity: 'error',
      title: 'Snippet not found on the page',
      description: withRemedy(SNIPPET_REMEDY),
    }
  }

  if (code === 'timeout' || code === 'unreachable') {
    return {
      severity: 'error',
      title: "Couldn't reach your site",
      description: withRemedy(UNREACHABLE_REMEDY),
    }
  }

  if (code === 'invalid_url') {
    return {
      severity: 'error',
      title: "That URL can't be checked",
      // The server message already says "Installation URL is not a public http(s)
      // address", so a remedy of "It must be a public http(s) address" rendered as
      // the same sentence twice in a row. A remedy has to add what to DO — the
      // server already covers what's wrong.
      description: withRemedy(
        'Use the address visitors reach from the open internet, not localhost, a bare IP, or an internal domain like .local or .test.',
      ),
    }
  }

  if (code.startsWith('http_')) {
    // 401/403 read as "we were blocked"; every other non-2xx is the page itself
    // erroring. Both are the site's response, not the snippet.
    const blocked = result.status_code === 401 || result.status_code === 403
    return {
      severity: 'error',
      title: blocked ? 'Your site blocked the check' : 'The page returned an error',
      description: withRemedy(blocked ? BLOCKED_REMEDY : UNREACHABLE_REMEDY),
    }
  }

  // No code at all — a client-side throw (network down, request cancelled) where
  // no server verdict was received. Don't guess a cause; the old code's mistake
  // was asserting one.
  return {
    severity: 'error',
    title: "Couldn't complete the check",
    description: serverMessage,
  }
}
